import moment from 'moment'
import type { IntlShape } from 'react-intl'

import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { TripStop } from 'tripPlanner/api/types'

export interface StopDuration {
  dayCount: number
  nightCount: number
}

export function isIsoDate(value: string): boolean {
  return moment(value, ISO_DATE_FORMAT, true).isValid()
}

export function getStopDurationDayCount(startDate: string, endDate: string): number | undefined {
  if (!isIsoDate(startDate) || !isIsoDate(endDate)) return undefined

  const start = moment(startDate, ISO_DATE_FORMAT, true)
  const end = moment(endDate, ISO_DATE_FORMAT, true)
  if (end.isBefore(start, 'day')) return undefined

  return end.diff(start, 'days') + 1
}

export function resolveStopDuration(stop: TripStop): StopDuration | undefined {
  if (stop.startDate && stop.endDate) {
    const dayCount = getStopDurationDayCount(stop.startDate, stop.endDate)
    return dayCount ? { dayCount, nightCount: dayCount - 1 } : undefined
  }

  if (stop.numberOfNights !== null && stop.numberOfNights !== undefined && stop.numberOfNights >= 0) {
    return { dayCount: stop.numberOfNights + 1, nightCount: stop.numberOfNights }
  }

  return undefined
}

export function formatStopDurationLabel(
  intl: IntlShape,
  duration: StopDuration,
): string {
  return intl.formatMessage(
    {
      id: 'f2JTw-htsp0bZywhsH-r',
      defaultMessage: '{dayCount, plural, one {# day} other {# days}} · {nightCount, plural, one {# night} other {# nights}}',
      description: 'Combined calendar day and overnight duration shown for a stop in the trip planner',
    },
    {
      dayCount: duration.dayCount,
      nightCount: duration.nightCount,
    },
  )
}

export function getStopDurationLabel(
  intl: IntlShape,
  stop: TripStop,
): string | undefined {
  const duration = resolveStopDuration(stop)
  return duration ? formatStopDurationLabel(intl, duration) : undefined
}
